import { useEffect, useState } from "react";
import { X, Play, Clapperboard } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLanguage, pick } from "@/i18n";
import { useSEO } from "@/hooks/useSEO";
import SectionHeader from "@/components/SectionHeader";


export default function Gallery() {
  const { lang, t } = useLanguage();
  const [lightbox, setLightbox] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  useSEO(t("gallery_seo_title"), t("gallery_seo_desc"));

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery-photos`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setPhotos)
      .catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/videos`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setVideos)
      .catch(() => {});
  }, []);

  return (
    <div data-testid="gallery-page">
      {/* HEADER */}
      <section className="py-20 lg:py-28 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-terracotta mb-4">Flegrea Experience</p>
            <h1 className="font-serif text-4xl md:text-6xl text-ink tracking-tight" data-testid="gallery-title">{t("gallery_title")}</h1>
            <p className="mt-6 text-ink/60 text-base md:text-lg leading-relaxed">{t("gallery_sub")}</p>
          </motion.div>
        </div>
      </section>

      {/* VIDEO */}
      <section className="pb-24" data-testid="gallery-videos">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader overline="Video" title={t("gallery_videos_title")} sub={t("gallery_videos_sub")} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            {videos.map((v, i) =>
              v.kind === "video" ? (
                <motion.div
                  key={v.video_id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7 }}
                  className="border border-stone2/60 bg-ivory"
                  data-testid={`gallery-video-${i}`}
                >
                  <div className="aspect-video overflow-hidden bg-ink">
                    <video controls preload="metadata" poster={v.poster || undefined} className="w-full h-full object-cover">
                      <source src={v.src} />
                    </video>
                  </div>
                  <p className="px-5 py-4 text-sm font-medium text-ink flex items-center gap-2">
                    <Play size={13} strokeWidth={1.5} className="text-medblue" /> {pick(v.title, lang)}
                  </p>
                </motion.div>
              ) : (
                <div
                  key={v.video_id}
                  data-testid={`gallery-video-coming-${i}`}
                  className="border border-dashed border-stone2 bg-sand/50 flex flex-col items-center justify-center aspect-auto min-h-[220px] px-6 text-center"
                >
                  <Clapperboard size={26} strokeWidth={1.25} className="text-ink/30 mb-4" />
                  <p className="text-sm font-medium text-ink/60">{pick(v.title, lang)}</p>
                  <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-terracotta">{t("gallery_coming_soon")}</p>
                </div>
              )
            )}
          </div>
        </div>
      </section>

      {/* PHOTOS */}
      <section className="pb-24 lg:pb-32" data-testid="gallery-photos">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader overline="Photo" title={t("gallery_photos_title")} />
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 mt-14 [column-fill:_balance]">
            {photos.map((p, i) => (
              <motion.button
                key={p.src}
                type="button"
                onClick={() => setLightbox(p)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.05 }}
                className="group relative block w-full mb-4 overflow-hidden text-left break-inside-avoid"
                data-testid={`gallery-photo-${i}`}
              >
                <img src={p.src} alt={pick(p.title, lang)} loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent px-3 pt-10 pb-3 text-[11px] font-medium text-ivory leading-snug">
                  {pick(p.title, lang)}
                </span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-ink/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button type="button" aria-label="Close" data-testid="gallery-lightbox-close" className="absolute top-5 right-5 text-ivory/70 hover:text-ivory transition-colors">
              <X size={26} strokeWidth={1.5} />
            </button>
            <figure className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <img src={lightbox.src} alt={pick(lightbox.title, lang)} className="w-full max-h-[80vh] object-contain" />
              <figcaption className="mt-4 text-center text-sm text-ivory/80">{pick(lightbox.title, lang)}</figcaption>
            </figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
